'use client';

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/Login.css';

function Login() {
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  
  // Login form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  
  // Register form state
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regUsername, setRegUsername] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regCategory, setRegCategory] = useState('waiter');
  const [regDailySalary, setRegDailySalary] = useState('500');

  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setLoading(true);

    try {
      const res = await login(username, password);
      const userRole = res.user?.role;
      if (userRole === 'employee') {
        navigate('/attendance');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setLoading(true);

    try {
      const payload = {
        name: regName,
        email: regEmail || regUsername,
        username: regUsername,
        password: regPassword,
        category: regCategory,
        dailySalary: Number(regDailySalary) || 500,
        role: 'employee'
      };

      const res = await register(payload);
      setSuccessMsg('Registration successful! Redirecting...');
      setTimeout(() => {
        if (res.user?.role === 'employee') {
          navigate('/attendance');
        } else {
          navigate('/dashboard');
        }
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h1>Catering Management System</h1>

        <div className="auth-tab-buttons">
          <button
            type="button"
            className={`auth-tab ${!isRegisterMode ? 'active' : ''}`}
            onClick={() => { setIsRegisterMode(false); setError(''); setSuccessMsg(''); }}
          >
            Login
          </button>
          <button
            type="button"
            className={`auth-tab ${isRegisterMode ? 'active' : ''}`}
            onClick={() => { setIsRegisterMode(true); setError(''); setSuccessMsg(''); }}
          >
            Employee Register
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}
        {successMsg && <div className="success-message">{successMsg}</div>}

        {!isRegisterMode ? (
          <form onSubmit={handleLoginSubmit}>
            <div className="form-group">
              <label htmlFor="username">Email or Username</label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter email or username"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
              />
            </div>

            <button type="submit" disabled={loading} className="login-btn">
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleRegisterSubmit}>
            <div className="form-group">
              <label htmlFor="regName">Full Name *</label>
              <input
                type="text"
                id="regName"
                value={regName}
                onChange={(e) => setRegName(e.target.value)}
                placeholder="e.g. Rahul Sharma"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="regUsername">Username / ID *</label>
              <input
                type="text"
                id="regUsername"
                value={regUsername}
                onChange={(e) => setRegUsername(e.target.value)}
                placeholder="e.g. rahul123"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="regEmail">Email Address (Optional)</label>
              <input
                type="email"
                id="regEmail"
                value={regEmail}
                onChange={(e) => setRegEmail(e.target.value)}
                placeholder="e.g. rahul@example.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="regPassword">Password *</label>
              <input
                type="password"
                id="regPassword"
                value={regPassword}
                onChange={(e) => setRegPassword(e.target.value)}
                placeholder="At least 6 characters"
                required
                minLength={6}
              />
            </div>

            <div className="form-group">
              <label htmlFor="regCategory">Role / Category</label>
              <select
                id="regCategory"
                value={regCategory}
                onChange={(e) => setRegCategory(e.target.value)}
                className="auth-select"
              >
                <option value="waiter">Waiter</option>
                <option value="chef">Chef</option>
                <option value="helper">Helper</option>
                <option value="manager">Manager</option>
                <option value="driver">Driver</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="regDailySalary">Expected Daily Salary (₹)</label>
              <input
                type="number"
                id="regDailySalary"
                value={regDailySalary}
                onChange={(e) => setRegDailySalary(e.target.value)}
                placeholder="500"
                min="0"
              />
            </div>

            <button type="submit" disabled={loading} className="login-btn">
              {loading ? 'Registering...' : 'Register as Employee'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default Login;
